import { clsx } from 'clsx'
import { ChevronRight, FileCheck2, Image as ImageIcon, Link2, MessageSquare } from 'lucide-react'
import type { JobAssignment, JobEquipment, JobWorkType } from '@/services/jobs.service'
import { isFUnitCategoryCode } from '@/utils/lxBxH'

const WORK_TYPE_LABELS: Record<JobWorkType, string> = {
  annual: 'Årlig',
  '48_months': '48-mnd',
  ndt: 'NDT',
  service: 'Service',
}

export const formatDate = (value: string | Date | null) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('nb-NO')
}

type NdtValidity = 'valid' | 'expiring' | 'expired' | 'unknown'

const startOfDay = (value: string | Date) => {
  const date = new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Valid until next NDT date, or last NDT + 4 years (same rule as the dashboard
 * search), compared with the planned inspection date (today if none).
 */
const ndtExpiry = (last: string | null, next: string | null): Date | null => {
  const source = next || last
  if (!source) return null
  const expiry = startOfDay(source)
  if (Number.isNaN(expiry.getTime())) return null
  if (!next) expiry.setFullYear(expiry.getFullYear() + 4)
  return expiry
}

const ndtValidity = (expiry: Date | null, inspectionDate: string | null): NdtValidity => {
  if (!expiry) return 'unknown'
  const reference = startOfDay(inspectionDate || new Date())
  if (Number.isNaN(reference.getTime())) reference.setTime(startOfDay(new Date()).getTime())
  const oneYearAfter = new Date(reference)
  oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1)

  if (expiry < reference) return 'expired'
  if (expiry <= oneYearAfter) return 'expiring'
  return 'valid'
}

const NDT_CLASSES: Record<NdtValidity, string> = {
  valid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expiring: 'bg-yellow-50 text-yellow-800 border-yellow-300',
  expired: 'bg-rose-50 text-rose-700 border-rose-200',
  unknown: 'bg-slate-50 text-slate-500 border-slate-200',
}

const NDT_RANK: Record<NdtValidity, number> = { expired: 0, expiring: 1, unknown: 2, valid: 3 }

interface NdtItem {
  id: string
  label: string | null
  validity: NdtValidity
  expiry: Date | null
}

/** Main equipment plus attached slings, each with its NDT validity. */
const ndtItems = (equipment: JobEquipment, inspectionDate: string | null): NdtItem[] =>
  [
    { id: equipment.id, label: null, last: equipment.last_ndt_date, next: equipment.next_ndt_date },
    ...equipment.sub_equipment
      .filter((sub) => sub.is_sling)
      .map((sub) => ({ id: sub.id, label: sub.serial_number || sub.name || 'sling', last: sub.last_ndt_date, next: sub.next_ndt_date })),
  ].map(({ id, label, last, next }) => {
    const expiry = ndtExpiry(last, next)
    return { id, label, expiry, validity: ndtValidity(expiry, inspectionDate) }
  })

const whoText = (items: NdtItem[]) => {
  const names = items.map((item) => (item.label ? `sling ${item.label}` : 'hovedutstyr'))
  if (items.length === 1 && !items[0].label) return ''
  return names.length > 2 ? ` · ${names.length} enheter` : ` · ${names.join(', ')}`
}

/**
 * One chip with the worst NDT state across the equipment and its slings.
 * Missing dates only count where NDT matters: F-units carrying slings.
 */
const NdtChip = ({ equipment, inspectionDate }: { equipment: JobEquipment; inspectionDate: string | null }) => {
  const ndtMatters = isFUnitCategoryCode(equipment.category_code) && equipment.sub_equipment.some((s) => s.is_sling)
  const items = ndtItems(equipment, inspectionDate).filter((item) => item.validity !== 'unknown' || ndtMatters)
  if (items.length === 0) return null

  const worst = items.reduce((a, b) => (NDT_RANK[b.validity] < NDT_RANK[a.validity] ? b : a)).validity
  const affected = items.filter((item) => item.validity === worst)
  const earliest = affected
    .flatMap((item) => item.expiry ?? [])
    .sort((a, b) => a.getTime() - b.getTime())[0]

  const text = {
    expired: `NDT utløpt${whoText(affected)}`,
    expiring: `NDT utløper ${formatDate(earliest)}${whoText(affected)}`,
    unknown: `NDT-dato mangler${whoText(affected)}`,
    valid: `NDT OK til ${formatDate(earliest)}`,
  }[worst]

  return (
    <span
      className={clsx(
        'inline-flex items-center h-6 px-2 rounded-md border text-xs font-semibold whitespace-nowrap max-w-full truncate',
        NDT_CLASSES[worst],
      )}
    >
      {text}
    </span>
  )
}

const SubEquipmentList = ({ equipment, inspectionDate }: { equipment: JobEquipment; inspectionDate: string | null }) => {
  const validityById = new Map(ndtItems(equipment, inspectionDate).map((item) => [item.id, item]))

  return (
    <ul className="mt-2 ml-0.5 border-l-2 border-slate-200 pl-3 space-y-1">
          {equipment.sub_equipment.map((sub) => {
            const ndt = sub.is_sling ? validityById.get(sub.id) : undefined
            return (
              <li key={sub.id} className="flex items-center gap-2 text-sm min-w-0">
                <Link2 size={13} className="text-[#0D9488] shrink-0" />
                <span className="font-medium text-slate-700 shrink-0">{sub.serial_number || '—'}</span>
                {sub.name && <span className="text-slate-500 truncate">{sub.name}</span>}
                {sub.wll && (
                  <span className="shrink-0 text-xs font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                    WLL {sub.wll}
                  </span>
                )}
                {ndt && (
                  <span className={clsx('shrink-0 text-xs font-semibold rounded border px-1.5 py-0.5', NDT_CLASSES[ndt.validity])}>
                    NDT {formatDate(sub.last_ndt_date) ?? '—'}
                  </span>
                )}
              </li>
            )
          })}
    </ul>
  )
}

export const equipmentLabel = (equipment: JobEquipment | null) =>
  equipment?.serial_number || equipment?.owner_id || equipment?.asset_number || 'Ukjent utstyr'

interface JobAssignmentRowProps {
  assignment: JobAssignment
  onOpen: () => void
  /** Status column, rendered to the right of the row. */
  statusControl: React.ReactNode
}

/** Pending work: full detail in three tiers (identity, NDT + attachments, notes). */
export const PendingAssignmentRow = ({ assignment, onOpen, statusControl }: JobAssignmentRowProps) => {
  const { equipment } = assignment
  const scheduled = formatDate(assignment.scheduled_date)

  return (
    <li className="flex items-stretch bg-white">
      <div className="flex-1 min-w-0 pl-9 pr-4 py-3">
        <button
          onClick={onOpen}
          disabled={!equipment}
          className="group w-full flex items-start gap-3 text-left rounded-md -mx-2 px-2 py-1 hover:bg-slate-50 disabled:cursor-not-allowed"
        >
          <div className="flex-1 min-w-0">
            <p className="flex items-baseline gap-2 min-w-0">
              <span className="text-base font-semibold text-slate-800 shrink-0">{equipmentLabel(equipment)}</span>
              <span className="text-sm text-slate-500 truncate">
                {equipment?.name || (equipment ? 'Uten navn' : 'Utstyret finnes ikke lenger')}
              </span>
            </p>
            {equipment && (
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <NdtChip equipment={equipment} inspectionDate={assignment.scheduled_date} />
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <div className="flex flex-col items-end gap-1">
              <span className="inline-flex items-center h-6 px-2 rounded text-xs font-semibold bg-[#1E3A5F]/10 text-[#1E3A5F]">
                {WORK_TYPE_LABELS[assignment.work_type] ?? assignment.work_type}
              </span>
              {scheduled && <span className="text-xs text-slate-400">{scheduled}</span>}
            </div>
            {equipment && <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500" />}
          </div>
        </button>

        {equipment && equipment.sub_equipment.length > 0 && (
          <SubEquipmentList equipment={equipment} inspectionDate={assignment.scheduled_date} />
        )}

        {assignment.comment && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-100 px-3 py-2">
            <MessageSquare size={14} className="text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 line-clamp-3">{assignment.comment}</p>
          </div>
        )}
      </div>
      <div className="shrink-0 flex border-l border-slate-100 bg-slate-50/60">{statusControl}</div>
    </li>
  )
}

/** Finished work: one line with the result. */
export const DoneAssignmentRow = ({ assignment, onOpen, statusControl }: JobAssignmentRowProps) => {
  const { equipment } = assignment
  return (
    <li className="flex items-stretch bg-white">
      <button
        onClick={onOpen}
        disabled={!equipment}
        className="flex-1 min-w-0 flex items-center gap-2 pl-9 pr-4 py-2 min-h-[52px] text-left hover:bg-slate-50 disabled:cursor-not-allowed"
      >
        <span className="text-sm font-semibold text-slate-600 shrink-0">{equipmentLabel(equipment)}</span>
        <span className="text-sm text-slate-400 truncate shrink">{equipment?.name}</span>
        {assignment.inspection_remarks && (
          <span className="text-sm text-slate-600 truncate min-w-0 flex-1" title={assignment.inspection_remarks}>
            · {assignment.inspection_remarks}
          </span>
        )}
        <span className="flex-1" />
        {assignment.inspection_images.length > 0 && (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-slate-500">
            <ImageIcon size={14} />
            {assignment.inspection_images.length}
          </span>
        )}
        {assignment.control_card_at && (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
            <FileCheck2 size={14} />
            Kort publisert
          </span>
        )}
      </button>
      <div className="shrink-0 flex border-l border-slate-100">{statusControl}</div>
    </li>
  )
}
